import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  ExclamationTriangleIcon, 
  XMarkIcon, 
  CheckCircleIcon,
  CurrencyDollarIcon 
} from '@heroicons/react/24/outline';
import axios from 'axios';
import UsageBasedPricingModal from './UsageBasedPricingModal';
import { isOrganizationAdmin } from '../../../utils/planPermissions';

const UsageLimitNotification = ({ 
  user, 
  usagePercentage, 
  usageAmount, 
  monthlyAllowance, 
  usageBasedPricingEnabled,
  subscriptionPlan,
  onDismiss,
  onToggleUsageBasedPricing,
  isVisible,
  isUsageBlocked = false,
  creditRefreshDate = null,
  onAcknowledgeBlocked = null
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);
    }
  }, [isVisible]);

  const handleDismiss = async () => {
    // If this is a blocking notification, use acknowledge instead
    if (isUsageBlocked && onAcknowledgeBlocked) {
      setIsAnimating(false);
      setTimeout(() => {
        onAcknowledgeBlocked();
      }, 300);
      return;
    }

    setIsDismissing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/team-billing/dismiss-usage-notification`,
        { usagePercentage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Animate out
      setIsAnimating(false);
      setTimeout(() => {
        onDismiss();
      }, 300);
    } catch (error) {
      console.error('Error dismissing notification:', error);
      setIsDismissing(false);
    }
  };

  const handleShowPricingModal = () => {
    setShowPricingModal(true);
  };

  const handleShowInfoModal = () => {
    setShowInfoModal(true);
  };

  const handleInfoModalToPricing = () => {
    setShowInfoModal(false);
    setShowPricingModal(true);
  };

  const handlePricingModalSuccess = (settings) => {
    // Animate out notification
    setIsAnimating(false);
    setTimeout(() => {
      onToggleUsageBasedPricing(true);
      onDismiss();
    }, 300);
  };

  const getNotificationConfig = () => {
    const percentageElement = <span className="font-semibold text-black dark:text-[#cecece]">{usagePercentage}%</span>;
    const isAdmin = isOrganizationAdmin();
    
    // Format credit refresh date
    const formatRefreshDate = (dateString) => {
      if (!dateString) return 'the next billing period';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      } catch (e) {
        return 'the next billing period';
      }
    };
    
    if (usagePercentage >= 100) {
      if (isUsageBlocked) {
        return {
          type: 'blocked',
          icon: ExclamationTriangleIcon,
          title: 'Monthly Limit Reached',
          message: (
            <>
              Your organization has used {percentageElement} of its monthly allowance. {isAdmin ? 
                `Midus services are temporarily unavailable until ${formatRefreshDate(creditRefreshDate)}.` :
                `Message sending is disabled until ${formatRefreshDate(creditRefreshDate)}. Contact your organization admin.`
              }
            </>
          ),
          urgency: 'critical'
        };
      } else {
        return {
          type: 'error',
          icon: ExclamationTriangleIcon,
          title: 'Monthly Limit Reached',
          message: (
            <>
              Your organization has used {percentageElement} of its monthly allowance. {usageBasedPricingEnabled ? 'Usage-based pricing is active.' : 'Consider enabling usage-based pricing.'}
            </>
          ),
          urgency: 'high'
        };
      }
    } else if (usagePercentage >= 95) {
      return {
        type: 'warning',
        icon: ExclamationTriangleIcon,
        title: 'Approaching Monthly Limit',
        message: (
          <>
            Your organization has used {percentageElement} of its monthly allowance. {usageBasedPricingEnabled ? 'Usage-based pricing is active.' : 'Consider enabling usage-based pricing.'}
          </>
        ),
        urgency: 'medium'
      };
    } else {
      return {
        type: 'info',
        icon: ExclamationTriangleIcon,
        title: 'Usage Notice',
        message: (
          <>
            Your organization has used {percentageElement} of its monthly allowance. {usageBasedPricingEnabled ? 'Usage-based pricing is active.' : 'Consider enabling usage-based pricing to avoid interruptions.'}
          </>
        ),
        urgency: 'low'
      };
    }
  };

  if (!isVisible) return null;

  const config = getNotificationConfig();
  const IconComponent = config.icon;

  return (
    <div className={`transition-all duration-300 ease-out ${
      isAnimating ? 'transform translate-y-0 opacity-100' : 'transform translate-y-4 opacity-0'
    }`}>
      <div className="bg-white dark:bg-[#181818] border border-[#e0e0e0] dark:border-[#282828] rounded-3xl p-4 shadow-lg w-full">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className="flex-shrink-0 text-[#737373] dark:text-[#999999]">
              <IconComponent className="w-5 h-5" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                {config.title}
              </h3>
              <p className="text-sm text-[#525252] dark:text-[#cccccc] leading-relaxed">
                {config.message}
              </p>
              
              {!usageBasedPricingEnabled && !isUsageBlocked && isOrganizationAdmin() && (
                <div className="mt-3 flex items-center gap-3">
                  <button
                    onClick={handleShowPricingModal}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-black dark:bg-white dark:text-black rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
                  >
                    <CurrencyDollarIcon className="w-4 h-4 mr-1" />
                    Enable Usage-Based Pricing
                  </button>
                  
                  <button
                    onClick={handleShowInfoModal}
                    className="text-sm text-[#525252] dark:text-[#cccccc] hover:text-[#181818] dark:hover:text-white transition-colors font-medium"
                  >
                    Learn More →
                  </button>
                </div>
              )}
              
              {isUsageBlocked && isOrganizationAdmin() && (
                <div className="mt-3 flex items-center gap-3">
                  <button
                    onClick={handleShowPricingModal}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 dark:bg-red-500 rounded-lg hover:bg-red-700 dark:hover:bg-red-600 transition-colors"
                  >
                    <CurrencyDollarIcon className="w-4 h-4 mr-1" />
                    Enable Usage-Based Pricing
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {!isUsageBlocked && (
            <button
              onClick={handleDismiss}
              disabled={isDismissing}
              className="ml-4 flex-shrink-0 text-[#737373] hover:text-[#525252] dark:hover:text-[#cccccc] transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
      
      {/* Info Modal */}
      {showInfoModal && createPortal(
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#111111] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-black dark:bg-white rounded-full flex items-center justify-center">
                    <CurrencyDollarIcon className="w-5 h-5 text-white dark:text-black" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      Usage-Based Pricing
                    </h2>
                    <p className="text-sm text-[#525252] dark:text-[#cccccc]">
                      Never run out of AI tokens again
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowInfoModal(false)}
                  className="text-[#737373] hover:text-[#525252] dark:hover:text-[#cccccc] transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                    What is Usage-Based Pricing?
                  </h3>
                  <p className="text-[#525252] dark:text-[#cccccc] leading-relaxed">
                    Pay for AI usage beyond your monthly allowance to avoid interruptions.
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">How it works:</h4>
                  <ul className="space-y-2 text-sm text-[#525252] dark:text-[#cccccc]">
                    <li>• Current monthly allowance: {
                      subscriptionPlan === 'solo' 
                        ? '$20' 
                        : subscriptionPlan === 'max' 
                        ? `$35/user on Max ($${monthlyAllowance} total for ${Math.round(monthlyAllowance / 35)} users)`
                        : subscriptionPlan === 'pro'
                        ? `$25/user on Pro ($${monthlyAllowance} total for ${Math.round(monthlyAllowance / 25)} users)`
                        : `$${monthlyAllowance} total`
                    }</li>
                    <li>• Additional usage charged when exceeded</li>
                    <li>• Optional spending caps available</li>
                    <li>• No service interruptions</li>
                  </ul>
                </div>

                <div className="pt-4 border-t border-[#e0e0e0] dark:border-[#282828]">
                  <p className="text-sm text-[#525252] dark:text-[#cccccc]">
                    Without this, AI access stops when you reach your monthly limit.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-8 pt-6 border-t border-[#e0e0e0] dark:border-[#282828]">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setShowInfoModal(false)}
                    className="px-4 py-2 text-sm text-[#525252] dark:text-[#cccccc] hover:text-[#181818] dark:hover:text-white transition-colors"
                  >
                    Maybe Later
                  </button>
                  <button
                    onClick={handleInfoModalToPricing}
                    className="inline-flex items-center px-4 py-2 bg-black dark:bg-white text-white dark:text-black text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
                  >
                    <CurrencyDollarIcon className="w-4 h-4 mr-2" />
                    Enable Usage-Based Pricing
                  </button>
                </div>
              </div>
                         </div>
           </div>
         </div>,
         document.body
       )}

      {/* Usage-Based Pricing Modal */}
      <UsageBasedPricingModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        onSuccess={handlePricingModalSuccess}
      />
    </div>
  );
};

export default UsageLimitNotification;